import DateTime from '@utils/mst/types/DateTime';
import { types } from 'mobx-state-tree';
import moment from 'moment-timezone';
import { getRootStore } from '@stores/ApplicationInterfaces';
import { getGroupById } from '@utils/mst/organizationalHelpers';

const MemberSearchResult = types.model(
  'MemberSearchResult',
  {
    id: types.number,
    acute: types.boolean,
    organization: types.string,
    name: types.maybeNull(types.string),
    status: types.string,
    startAt: types.maybeNull(DateTime),
    lastMessageTimestamp: types.maybeNull(DateTime),
    organizationalGroup: types.maybeNull(types.frozen()),
    services: types.array(types.number),
    defaultOrganizationalGroup: types.maybeNull(types.frozen()),
  },
)
  .views((self) => ({
    get startAtDisplayTimestamp() {
      return moment(self.startAt).format('YYYY-MM-DD hh:mma');
    },

    get lastMessagetDisplayTimestamp() {
      return self.lastMessageTimestamp ? moment(self.lastMessageTimestamp).format('YYYY-MM-DD hh:mma') : 'No Message Sent';
    },

    get canBeClicked() {
      const root = getRootStore(self);
      const ringService = Array.from(root.organizationalServices.values()).find(s => s.name.toLowerCase() === 'ring');
      const ringServiceId = ringService?.id;

      // Use effectiveServices to get the current services (from organizational group object or services array)
      const currentServices = this.effectiveServices;

      // Disable click if member has only Ring service
      const hasOnlyRingService = currentServices.length === 1 && ringServiceId && currentServices.includes(ringServiceId);

      return !hasOnlyRingService;
    },

    get effectiveOrganizationalGroup() {
      let groupId: number | null = null;
      if (typeof self.organizationalGroup === 'number') {
        groupId = self.organizationalGroup;
      } else if (self.organizationalGroup && typeof self.organizationalGroup === 'object') {
        groupId = self.organizationalGroup.id;
      }

      if (!groupId) return 'Coaching-Only';

      const root = getRootStore(self);
      const org = Array.from(root.organizations.values()).find(o => o.name === self.organization);
      if (!org) return 'Coaching-Only';

      const group = getGroupById(groupId, root.organizationalGroups);
      return group?.name || 'Coaching-Only';
    },

    get serviceNames() {
      const root = getRootStore(self);

      // If we have services from the organizational group object, use those
      if (self.organizationalGroup && typeof self.organizationalGroup === 'object' && self.organizationalGroup.services) {
        return self.organizationalGroup.services.map((service: any) => service.name);
      }

      // Fallback to the services array
      return self.services.map(serviceId => {
        const service = Array.from(root.organizationalServices.values()).find(s => s.id === serviceId);
        return service?.name || serviceId;
      });
    },

    // Get the actual services array from the organizational group if available
    get effectiveServices() {
      if (self.organizationalGroup && typeof self.organizationalGroup === 'object' && self.organizationalGroup.services) {
        return self.organizationalGroup.services.map((service: any) => service.id);
      }
      return self.services;
    },
  }))
  .actions(self => ({
    setOrganizationalGroup(groupId: number | null) {
      self.organizationalGroup = groupId;
    },

    setServices(serviceIds: number[]) {
      self.services.replace(serviceIds);
    }
  }));

export default MemberSearchResult;
